import { format, addMinutes, parseISO } from 'date-fns';

// Fixed 15-min slots del Day Planner. Movido desde Planner.js para que
// `buildDayViewState` (y todo lo que dependa de la duración de slot) tenga
// una única fuente.
export const SLOT_DURATION = 15;

// Deriva el estado de la vista de día del Planner (availabilityMap, bookings,
// breaks) a partir de las respuestas crudas de /availability, /bookings y
// /breaks. Movido literalmente desde el camino de éxito de `fetchData` en
// Planner.js (D1): función pura para que `fetchData` y el refresco automático
// (R1) produzcan el mismo estado a partir del mismo payload.
export function buildDayViewState({ availabilityRes, bookingsRes, breaksRes, isClient, t }) {
  const ownBookings = bookingsRes.data;

  let availabilityMap;
  let bookings;

  if (availabilityRes?.data?.slots) {
    // Build per-slot occupancy + blockedReason map for ALL roles
    const aMap = {};
    const blockedMap = {};
    const staffMap = {};
    for (const slot of availabilityRes.data.slots) {
      const slotDate = parseISO(slot.datetime);
      const hhmm = format(slotDate, 'HH:mm');
      for (const cal of (slot.calendars || [])) {
        if (!aMap[cal.id]) aMap[cal.id] = {};
        if (!blockedMap[cal.id]) blockedMap[cal.id] = {};
        if (!staffMap[cal.id]) staffMap[cal.id] = {};
        aMap[cal.id][hhmm] = cal.occupied || 0;
        staffMap[cal.id][hhmm] = cal.staffAvailable ?? 1;
        if (cal.blockedReason) {
          blockedMap[cal.id][hhmm] = cal.blockedReason;
        }
      }
    }
    availabilityMap = { ...aMap, __blocked: blockedMap, __staff: staffMap };

    // Client only: build placeholders for occupied slots
    if (isClient) {
      const placeholders = [];

      const toLocalNaive = (d) => d instanceof Date ? d : new Date(d);

      const overlapsSlot = (b, slotStart) => {
        try {
          const bStart = toLocalNaive(parseISO(b.startDateTime));
          const duration = (b.durationMinutes != null)
            ? Number(b.durationMinutes)
            : (SLOT_DURATION * Number(b.slotsCount || 1));
          const bEnd = addMinutes(bStart, duration);
          return bStart <= slotStart && slotStart < bEnd;
        } catch (e) {
          console.error('overlapsSlot parse error', e);
          return false;
        }
      };

      for (const slot of availabilityRes.data.slots) {
        const slotStart = toLocalNaive(parseISO(slot.datetime));

        for (const cal of (slot.calendars || [])) {
          const ownOverlap = ownBookings.filter(b =>
            b.calendarId === cal.id && overlapsSlot(b, slotStart)
          ).length;

          const others = Math.max(0, (cal.occupied || 0) - ownOverlap);

          if (others > 0) {
            placeholders.push({
              id: `occupied-${cal.id}-${slot.datetime}`,
              calendarId: cal.id,
              startDateTime: slot.datetime,
              durationMinutes: SLOT_DURATION,
              status: 'RESERVED',
              clientName: t('planner.occupied'),
              isPlaceholder: true,
            });
          }
        }
      }

      // Merge consecutive placeholders per calendar
      const merged = [];
      const sorted = placeholders.slice().sort((a, b) => {
        if (a.calendarId !== b.calendarId) return a.calendarId.localeCompare(b.calendarId);
        return new Date(a.startDateTime) - new Date(b.startDateTime);
      });

      for (const p of sorted) {
        const last = merged[merged.length - 1];
        if (!last) { merged.push({ ...p }); continue; }
        if (last.calendarId === p.calendarId) {
          const lastEnd = addMinutes(parseISO(last.startDateTime), Number(last.durationMinutes || SLOT_DURATION));
          if (lastEnd.getTime() === parseISO(p.startDateTime).getTime()) {
            last.durationMinutes = Number(last.durationMinutes || SLOT_DURATION) + SLOT_DURATION;
            continue;
          }
        }
        merged.push({ ...p });
      }

      bookings = [...ownBookings, ...merged];
    } else {
      bookings = ownBookings;
    }
  } else {
    bookings = ownBookings;
    availabilityMap = {};
  }

  const breaks = breaksRes?.data || [];

  return { availabilityMap, bookings, breaks };
}
